import type { ChatMessageSource } from '../../../types/chatMessage';
import {
  internetSourceTitle,
  isInternetSource,
  isKnowledgeBackedSource,
  isSourceKnowledgeBaseInaccessible,
  isTrainingExampleDeleted,
  isTrainingExampleSource,
  resolveSourceKnowledgeBaseId,
  sourceFileTitle,
  trainingExampleTitle,
} from './chatMessageSourceUtils';

export type LiksChatMessageSourceRowProps = {
  source: ChatMessageSource;
  getSourceTypeLabel: (sourceType: string | undefined) => string;
  openDocumentLabel: string;
  openTrainingExampleLabel?: string;
  sourceInaccessibleLabel: string;
  sourceTrainingDeletedLabel?: string;
  fallbackKnowledgeBaseId?: string;
  knowledgeBases?: ReadonlyArray<{ id: string; name: string }>;
  accessibleKnowledgeBaseIds: Set<string> | null;
  onOpenDocument: (source: ChatMessageSource) => void;
  onOpenTrainingExample?: (source: ChatMessageSource) => void;
};

export function LiksChatMessageSourceRow({
  source,
  getSourceTypeLabel,
  openDocumentLabel,
  openTrainingExampleLabel,
  sourceInaccessibleLabel,
  sourceTrainingDeletedLabel,
  fallbackKnowledgeBaseId,
  knowledgeBases,
  accessibleKnowledgeBaseIds,
  onOpenDocument,
  onOpenTrainingExample,
}: LiksChatMessageSourceRowProps) {
  const typeLabel = getSourceTypeLabel(source.sourceType);
  const isInternet = isInternetSource(source.sourceType);
  const isTraining = isTrainingExampleSource(source.sourceType);
  const fileTitle = isKnowledgeBackedSource(source.sourceType)
    ? sourceFileTitle(source)
    : '';
  const trainingTitle = isTraining ? trainingExampleTitle(source) : '';
  const sourceUrl = source.sourceUrl?.trim() ?? '';
  const internetTitle = isInternet ? internetSourceTitle(source) : '';
  const showUrl =
    (source.sourceType === 'CRAWL' || isInternet) && sourceUrl.length > 0;
  const isInaccessible = isSourceKnowledgeBaseInaccessible(
    source,
    accessibleKnowledgeBaseIds,
  );
  const documentId = source.documentId?.trim() ?? '';
  const trainingExampleId = source.trainingExampleId?.trim() ?? '';
  const knowledgeBaseId = resolveSourceKnowledgeBaseId(
    source,
    fallbackKnowledgeBaseId,
    knowledgeBases,
  );
  const canOpenDocument =
    isKnowledgeBackedSource(source.sourceType) &&
    !isInaccessible &&
    documentId.length > 0 &&
    knowledgeBaseId.length > 0;
  const trainingDeleted = isTraining && isTrainingExampleDeleted(source);
  const canOpenTrainingExample =
    isTraining && trainingExampleId.length > 0 && onOpenTrainingExample != null;
  const trainingDisplayTitle =
    trainingTitle.length > 0
      ? trainingTitle
      : (openTrainingExampleLabel ?? typeLabel);

  return (
    <tr>
      <td className="liks-chat-message__sources-type-cell">{typeLabel}</td>
      <td>
        {isInaccessible ? (
          <div className="liks-chat-message__sources-file">
            {fileTitle.length > 0 && (
              <span className="liks-chat-message__sources-name">
                {fileTitle}
              </span>
            )}
            <p className="liks-chat-message__sources-inaccessible">
              {sourceInaccessibleLabel}
            </p>
          </div>
        ) : canOpenDocument && fileTitle.length > 0 ? (
          <button
            type="button"
            className="liks-chat-message__sources-document"
            aria-label={`${openDocumentLabel}: ${fileTitle}`}
            onClick={() => onOpenDocument(source)}
          >
            {fileTitle}
          </button>
        ) : fileTitle.length > 0 ? (
          <span className="liks-chat-message__sources-name">{fileTitle}</span>
        ) : canOpenTrainingExample ? (
          <div className="liks-chat-message__sources-file">
            <button
              type="button"
              className="liks-chat-message__sources-document"
              aria-label={`${openTrainingExampleLabel ?? openDocumentLabel}: ${trainingDisplayTitle}`}
              onClick={() => onOpenTrainingExample(source)}
            >
              {trainingDisplayTitle}
            </button>
            {trainingDeleted && sourceTrainingDeletedLabel != null && (
              <p className="liks-chat-message__sources-inaccessible">
                {sourceTrainingDeletedLabel}
              </p>
            )}
          </div>
        ) : trainingTitle.length > 0 || trainingDeleted ? (
          <div className="liks-chat-message__sources-file">
            {trainingDisplayTitle.length > 0 && (
              <span className="liks-chat-message__sources-name">
                {trainingDisplayTitle}
              </span>
            )}
            {trainingDeleted && sourceTrainingDeletedLabel != null && (
              <p className="liks-chat-message__sources-inaccessible">
                {sourceTrainingDeletedLabel}
              </p>
            )}
          </div>
        ) : null}
      </td>
      <td>
        {showUrl ? (
          <a
            className="liks-chat-message__sources-link"
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {isInternet && internetTitle.length > 0 ? internetTitle : sourceUrl}
          </a>
        ) : isInternet && internetTitle.length > 0 ? (
          <span className="liks-chat-message__sources-name">
            {internetTitle}
          </span>
        ) : null}
      </td>
    </tr>
  );
}
